import { useSyncExternalStore } from 'react';

/** Aba de rotina aberta (07 §2.7). */
export interface AbaRotina {
  rotina: string;
  titulo: string;
  /** Última rota visitada dentro da rotina (lista, formulário…), para voltar ao mesmo lugar. */
  caminho: string;
  pendente: boolean;
  usadaEm: number;
}

export const MAXIMO_ABAS = 12;

/** Limite da instalacao: 1 a 12 abas. Valor ausente ou invalido usa 12. */
export function normalizarLimiteAbas(configuracao: string | undefined): number {
  const valor = Number(configuracao);
  return Number.isInteger(valor) && valor >= 1 && valor <= MAXIMO_ABAS ? valor : MAXIMO_ABAS;
}

/** O caminho pertence à rotina (lista, novo, detalhe, diagnóstico): /r/CODIGO ou /r/CODIGO/… — só ele vira o "último lugar" da aba. */
export function daRotina(caminho: string, codigo: string) {
  const prefixo = `/r/${codigo}`.toUpperCase();
  const c = caminho.toUpperCase();
  return c === prefixo || c.startsWith(`${prefixo}/`);
}

export const limiteAbas = normalizarLimiteAbas(import.meta.env.VITE_LIMITE_ABAS);

let abas: AbaRotina[] = [];
let ativa: string | null = null;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((f) => f());
let instantaneo: { abas: AbaRotina[]; ativa: string | null } = { abas, ativa };

function publicar() {
  instantaneo = { abas, ativa };
  avisar();
}

export const lojaAbas = {
  assinar(f: () => void) {
    ouvintes.add(f);
    return () => ouvintes.delete(f);
  },
  ler: () => instantaneo,

  /** Abre ou ativa. Devolve false quando o limite configurado foi atingido. */
  abrir(rotina: string, titulo: string, caminho: string): boolean {
    const existente = abas.find((a) => a.rotina === rotina);
    if (existente) {
      abas = abas.map((a) => (a.rotina === rotina ? { ...a, titulo: titulo || a.titulo, caminho, usadaEm: Date.now() } : a));
    } else {
      if (abas.length >= limiteAbas) return false;
      abas = [...abas, { rotina, titulo, caminho, pendente: false, usadaEm: Date.now() }];
    }
    ativa = rotina;
    publicar();
    return true;
  },

  fechar(rotina: string): AbaRotina | null {
    const i = abas.findIndex((a) => a.rotina === rotina);
    if (i < 0) return null;
    abas = abas.filter((a) => a.rotina !== rotina);
    if (ativa === rotina) ativa = (abas[i] ?? abas[i - 1])?.rotina ?? null;
    publicar();
    return abas.find((a) => a.rotina === ativa) ?? null;
  },

  /** A usada há mais tempo sem alteração pendente — a que o aviso da 13ª aba oferece fechar. */
  maisAntigaLivre(): AbaRotina | null {
    return [...abas].filter((a) => !a.pendente).sort((x, y) => x.usadaEm - y.usadaEm)[0] ?? null;
  },

  marcarPendente(rotina: string, pendente: boolean) {
    if (abas.find((a) => a.rotina === rotina)?.pendente === pendente) return;
    abas = abas.map((a) => (a.rotina === rotina ? { ...a, pendente } : a));
    publicar();
  },

  desativar() {
    if (ativa === null) return;
    ativa = null;
    publicar();
  },

  limpar() {
    abas = [];
    ativa = null;
    publicar();
  },
};

export function useAbas() {
  return useSyncExternalStore(lojaAbas.assinar, lojaAbas.ler, lojaAbas.ler);
}
